/**
 * Liberação de Visitas — MORADOR (Portal), 0.1.65.
 *
 * O morador: (1) cria CONVITES (gera QR + código para mandar ao visitante),
 * (2) decide as chegadas PRESENCIAIS que a portaria mandou (Autorizar/Negar),
 * (3) mantém a lista de AUTORIZADOS permanentes (diarista, familiar…).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, BadgeCheck, Check, Loader2, Plus, QrCode, Share2, ShieldCheck,
  Trash2, UserPlus, X,
} from "lucide-react";
import { api, ApiError } from "../lib/api";
import { fmtDataHora as fmt } from "../lib/datas";

interface Visita {
  id: number; unidade: string; visitante_nome: string; documento: string | null;
  placa: string | null; tipo: string | null; origem: string; observacao: string | null;
  codigo: string | null; status: string; validade_inicio: string | null;
  validade_fim: string | null; recorrente: boolean; criado_em: string | null;
  aprovado_por: string | null; entrada_em: string | null; saida_em: string | null;
  qr?: string | null;
}
interface Resp {
  condominio_id: number; itens: Visita[]; pode_gerir: boolean;
  unidades: string[]; unidades_morador: string[]; tipos: string[]; status_possiveis: string[];
}
interface Autorizado {
  id: number; unidade_norm: string; nome: string; documento: string | null;
  tipo: string | null; observacao: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  convidado: "Convite ativo", aguardando: "Aguardando você", autorizada: "Autorizada",
  negada: "Negada", dentro: "Entrou", encerrada: "Encerrada", expirada: "Expirada",
};

export function VisitasMoradorPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["visitas-morador", condominioId] });
    qc.invalidateQueries({ queryKey: ["visitantes-autorizados", condominioId] });
  };
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ["visitas-morador", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/visitas`),
  });
  const { data: autz } = useQuery<Autorizado[]>({
    queryKey: ["visitantes-autorizados", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/visitantes-autorizados`),
  });

  // ---- criar convite ----
  const [novoConvite, setNovoConvite] = useState(false);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [validadeFim, setValidadeFim] = useState("");
  const [recorrente, setRecorrente] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [qrModal, setQrModal] = useState<Visita | null>(null);

  const minhas = data?.unidades_morador ?? [];
  const precisaUnidade = !!data?.pode_gerir && minhas.length === 0;
  const [unidadeSel, setUnidadeSel] = useState("");

  const criar = useMutation({
    mutationFn: () => api.post<Visita>(`/condominios/${condominioId}/visitas`, {
      visitante_nome: nome.trim(), tipo: tipo || null,
      validade_fim: validadeFim ? new Date(validadeFim).toISOString() : null,
      recorrente,
      unidade: precisaUnidade ? unidadeSel : undefined,
    }),
    onSuccess: (v) => {
      setNovoConvite(false); setNome(""); setTipo(""); setValidadeFim(""); setRecorrente(false);
      setUnidadeSel(""); setErro(null); inval(); setQrModal(v);
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível criar o convite."),
  });

  const decidir = useMutation({
    mutationFn: ({ id, acao }: { id: number; acao: "aprovar" | "negar" }) =>
      api.post(`/visitas/${id}/${acao}`, {}),
    onSuccess: inval,
  });
  const verQr = useMutation({
    mutationFn: (id: number) => api.get<Visita>(`/visitas/${id}/qr`),
    onSuccess: (v) => setQrModal(v),
  });

  // ---- autorizados permanentes ----
  const [novoAut, setNovoAut] = useState(false);
  const [autNome, setAutNome] = useState("");
  const [autTipo, setAutTipo] = useState("");
  const addAut = useMutation({
    mutationFn: () => api.post(`/condominios/${condominioId}/visitantes-autorizados`, {
      nome: autNome.trim(), tipo: autTipo.trim() || null,
      unidade: precisaUnidade ? unidadeSel : undefined,
    }),
    onSuccess: () => { setNovoAut(false); setAutNome(""); setAutTipo(""); inval(); },
  });
  const delAut = useMutation({
    mutationFn: (id: number) => api.delete(`/visitantes-autorizados/${id}`),
    onSuccess: inval,
  });

  const itens = data?.itens ?? [];
  const pendentes = itens.filter((v) => v.status === "aguardando");
  const convites = itens.filter((v) => v.status === "convidado" || v.status === "autorizada");
  const historico = itens.filter((v) => ["encerrada", "negada", "expirada", "dentro"].includes(v.status));

  const compartilhar = (v: Visita) => {
    const texto =
      `Convite de visita\nVisitante: ${v.visitante_nome}\nCódigo na portaria: ${v.codigo}` +
      (v.validade_fim ? `\nVálido até: ${fmt(v.validade_fim)}` : "");
    if (navigator.share) {
      navigator.share({ title: "Convite de visita", text: texto }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank");
    }
  };

  return (
    <div className="max-w-lg mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-slate-700 font-semibold"><ShieldCheck size={18} /> Minhas Visitas</div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-10"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
      ) : (
        <>
          {/* Pendentes de decisão (chegada presencial) */}
          {pendentes.length > 0 && (
            <div className="space-y-2.5 mb-5">
              {pendentes.map((v) => (
                <div key={v.id} className="p-4 rounded-2xl border border-amber-300 bg-amber-50">
                  <div className="font-semibold text-slate-800">🔔 {v.visitante_nome} está na portaria</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Unidade {v.unidade}{v.tipo ? ` · ${v.tipo}` : ""} · chegou {fmt(v.criado_em)}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => decidir.mutate({ id: v.id, acao: "aprovar" })} disabled={decidir.isPending}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                      <Check size={16} /> Autorizar
                    </button>
                    <button onClick={() => decidir.mutate({ id: v.id, acao: "negar" })} disabled={decidir.isPending}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-rose-300 text-rose-700 bg-white text-sm font-medium">
                      <X size={16} /> Negar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Criar convite */}
          {!novoConvite ? (
            <button onClick={() => { setNovoConvite(true); setErro(null); }}
              className="mb-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-medium">
              <Plus size={18} /> Convidar visitante (gera QR)
            </button>
          ) : (
            <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-white space-y-2.5">
              {precisaUnidade && (
                <select value={unidadeSel} onChange={(e) => setUnidadeSel(e.target.value)}
                  className="w-full text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
                  <option value="">Escolha o apartamento…</option>
                  {(data?.unidades ?? []).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
              <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do visitante"
                className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
              <div className="grid grid-cols-2 gap-2">
                <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                  className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
                  <option value="">Tipo (opcional)</option>
                  {(data?.tipos ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label className="text-xs text-slate-500 flex flex-col gap-0.5">Válido até (opcional)
                  <input type="datetime-local" value={validadeFim} onChange={(e) => setValidadeFim(e.target.value)}
                    className="text-sm rounded-md border border-slate-300 px-2 py-1.5" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={recorrente} onChange={(e) => setRecorrente(e.target.checked)} />
                Recorrente (diarista/prestador — pode entrar várias vezes)
              </label>
              {erro && <div className="text-sm text-rose-700">{erro}</div>}
              <div className="flex gap-2">
                <button onClick={() => criar.mutate()} disabled={criar.isPending || !nome.trim() || (precisaUnidade && !unidadeSel)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm disabled:opacity-50">
                  {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />} Gerar convite
                </button>
                <button onClick={() => { setNovoConvite(false); setErro(null); }}
                  className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm">Cancelar</button>
              </div>
            </div>
          )}

          {/* Convites ativos */}
          {convites.length > 0 && (
            <div className="space-y-2 mb-5">
              <h2 className="text-sm font-semibold text-slate-700">Convites ativos</h2>
              {convites.map((v) => (
                <div key={v.id} className="p-3 rounded-xl border border-teal-200 bg-teal-50/60 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{v.visitante_nome}{v.recorrente ? " · recorrente" : ""}</div>
                    <div className="text-xs text-slate-500">
                      código <span className="font-mono font-semibold text-teal-800">{v.codigo}</span>
                      {v.validade_fim ? ` · até ${fmt(v.validade_fim)}` : ""}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => verQr.mutate(v.id)} title="Ver QR"
                      className="p-2 rounded-md border border-teal-300 text-teal-700 bg-white hover:bg-teal-50"><QrCode size={16} /></button>
                    <button onClick={() => compartilhar(v)} title="Compartilhar"
                      className="p-2 rounded-md border border-teal-300 text-teal-700 bg-white hover:bg-teal-50"><Share2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Autorizados permanentes */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-700">Autorizados permanentes</h2>
              {!novoAut && (
                <button onClick={() => setNovoAut(true)}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-teal-700 text-white"><UserPlus size={14} /> Adicionar</button>
              )}
            </div>
            {novoAut && (
              <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-2 mb-2">
                {precisaUnidade && (
                  <select value={unidadeSel} onChange={(e) => setUnidadeSel(e.target.value)}
                    className="w-full text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
                    <option value="">Escolha o apartamento…</option>
                    {(data?.unidades ?? []).map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <input value={autNome} onChange={(e) => setAutNome(e.target.value)} placeholder="Nome"
                    className="text-sm rounded-md border border-slate-300 px-3 py-2" />
                  <input value={autTipo} onChange={(e) => setAutTipo(e.target.value)} placeholder="Ex.: diarista, filho(a)"
                    className="text-sm rounded-md border border-slate-300 px-3 py-2" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => addAut.mutate()} disabled={addAut.isPending || !autNome.trim() || (precisaUnidade && !unidadeSel)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-700 text-white text-sm disabled:opacity-50">
                    {addAut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
                  </button>
                  <button onClick={() => setNovoAut(false)} className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 text-sm">Cancelar</button>
                </div>
              </div>
            )}
            {(autz ?? []).length === 0 ? (
              <p className="text-xs text-slate-400">Ninguém cadastrado. A portaria libera pessoas desta lista sem te incomodar.</p>
            ) : (
              <div className="space-y-1.5">
                {(autz ?? []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-white">
                    <div className="text-sm text-slate-700 flex items-center gap-1.5">
                      <BadgeCheck size={15} className="text-teal-600" /> {a.nome}
                      {a.tipo ? <span className="text-slate-400"> · {a.tipo}</span> : null}
                    </div>
                    <button onClick={() => { if (confirm(`Remover ${a.nome}?`)) delAut.mutate(a.id); }}
                      className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Histórico */}
          {historico.length > 0 && (
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-slate-700 mb-2">Histórico</h2>
              <div className="space-y-1.5">
                {historico.slice(0, 20).map((v) => (
                  <div key={v.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600">
                    <span className="truncate">{v.visitante_nome}</span>
                    <span className="text-xs text-slate-400">{STATUS_LABEL[v.status] ?? v.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal do QR */}
      {qrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900 mb-1">{qrModal.visitante_nome}</h3>
            <p className="text-xs text-slate-500 mb-3">Mostre este QR (ou o código) na portaria.</p>
            {qrModal.qr ? (
              <img src={qrModal.qr} alt="QR do convite" className="w-52 h-52 mx-auto rounded-lg border border-slate-200" />
            ) : (
              <div className="text-sm text-slate-500 py-6">QR indisponível — use o código abaixo.</div>
            )}
            <div className="mt-3 text-2xl font-mono font-bold tracking-widest text-teal-800">{qrModal.codigo}</div>
            {qrModal.validade_fim && <div className="text-xs text-slate-500 mt-1">Válido até {fmt(qrModal.validade_fim)}</div>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => compartilhar(qrModal)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-teal-700 text-white text-sm"><Share2 size={16} /> Compartilhar</button>
              <button onClick={() => setQrModal(null)} className="px-3 py-2 rounded-md border border-slate-300 text-slate-600 text-sm">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
